import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { DAYS_OF_WEEK, dateTimeFormatForRequest, defaultDate, defaultDateFormat, fullWeek } from '~/constants/dateTime';
import { localeStore } from '~/mobx/localesStore';
import { IWeekModalSaveParams } from '~/mobx/modalStore/types';
import { promobannerStore } from '../../../store/promobannerStore';
import { TPromoBannerValiditySettings } from '../../../store/types';

export const usePromoBannerValidityHook = (): [
  string,
  Date | undefined,
  Date | undefined,
  DAYS_OF_WEEK[] | undefined,
  IWeekModalSaveParams['timeByDays'],
  (params: IWeekModalSaveParams) => void
] => {
  const startDate = useMemo(
    () => (promobannerStore.promobanner.start_date ? new Date(promobannerStore.promobanner.start_date * 1000) : undefined),
    [promobannerStore.promobanner.start_date]
  );

  const endDate: Date | undefined = useMemo(() => {
    if (!promobannerStore.promobanner.end_date || moment.unix(promobannerStore.promobanner.end_date || 0).isAfter(moment('2120-01-01'))) {
      return undefined;
    }

    return new Date(promobannerStore.promobanner.end_date * 1000);
  }, [promobannerStore.promobanner.end_date]);

  const placeholder = useMemo(
    () =>
      promobannerStore.promobanner.start_date
        ? `${moment(startDate).format(defaultDateFormat)}  –  ${endDate ? moment(endDate).format(defaultDateFormat) : defaultDate}`
        : localeStore.t('choose_date'),
    [promobannerStore.promobanner.start_date, promobannerStore.promobanner.end_date]
  );

  const settings = useMemo(
    () =>
      fullWeek?.reduce(
        (acc, day) => {
          const item = promobannerStore.promobanner.timeslots?.find((slot) => slot.weekday === day);
          if (item) {
            acc.timeByDays.push([
              item.weekday,
              {
                startTime: castStringToDate(startDate, item.start_time),
                endTime: castStringToDate(startDate, item.end_time),
                isValid: true,
              },
            ]);

            if (item.active) {
              acc.daysOfWeek.push(item.weekday);
            }
          } else {
            // Default timeslots
            acc.timeByDays.push([
              day,
              {
                startTime: moment({ hour: 0, minute: 0, second: 0 }).toDate(),
                endTime: moment({ hour: 23, minute: 59, second: 0 }).toDate(),
                isValid: true,
              },
            ]);
            // If it's creating new promo fill all days
            if (!promobannerStore.promobanner.timeslots?.length) {
              acc.daysOfWeek.push(day);
            }
          }

          return acc;
        },
        { timeByDays: [], daysOfWeek: [] } as {
          daysOfWeek: DAYS_OF_WEEK[];
          timeByDays: [DAYS_OF_WEEK, { startTime?: Date; endTime?: Date; isValid: boolean; [key: string]: any }][];
        }
      ),
    [promobannerStore.promobanner.timeslots]
  );

  const onSave = useCallback((params: IWeekModalSaveParams) => {
    const timeslots = params.timeByDays
      ? Array.from(params.timeByDays).map(([day, item]) => {
          const active = params.daysOfWeek?.includes(day);
          return {
            active,
            weekday: day,
            start_time: moment(item.startTime).format('HH:mm:ss'),
            end_time: moment(item.endTime).format('HH:mm:ss'),
          } as TPromoBannerValiditySettings;
        })
      : [];

    console.log(params);
    promobannerStore.changePromobanner(
      {
        start_date: params.startDate ? moment(params.startDate).unix() : undefined,
        end_date: params.endDate ? moment(params.endDate).unix() : undefined,
        timeslots,
        active_24_hours: Boolean(params.allDay),
      },
      true
    );
  }, []);

  return [placeholder, startDate, endDate, settings?.daysOfWeek, new Map(settings?.timeByDays), onSave];
};

const castStringToDate = (date: Date | undefined, time: string): Date =>
  moment(`${moment(date).format('YYYY-MM-DD')} ${time}`, dateTimeFormatForRequest)
    .local()
    .toDate();
